import { useState, useRef, useEffect, useCallback } from 'react'
import { useRoomContext, useLocalParticipant } from '@livekit/components-react'
import { RoomEvent } from 'livekit-client'
import { apiFetch } from '../lib/apiFetch'
import { createLogger } from '../lib/logger'

const slog = createLogger('visio-chat')

interface VisioDataChatProps {
  onClose: () => void
  channelId?: string
  isGuest?: boolean
}

interface ChatMsg {
  id: string
  sender: string
  senderName: string
  text: string
  ts: number
}

interface ChatDataMessage {
  type: 'visio-chat'
  action: 'message' | 'sync'
  messages?: ChatMsg[]
  message?: ChatMsg
}

export function VisioDataChat({ onClose, channelId, isGuest }: VisioDataChatProps) {
  const room = useRoomContext()
  const { localParticipant } = useLocalParticipant()
  const [messages, setMessages] = useState<ChatMsg[]>([])
  const [input, setInput] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)
  const encoder = useRef(new TextEncoder())
  const decoder = useRef(new TextDecoder())
  const historyRef = useRef<ChatMsg[]>([])
  const syncReceived = useRef(false)
  const savedIds = useRef<Set<string>>(new Set())

  // Persist a message to the messaging API (authenticated users only)
  const persistToMessaging = useCallback(async (msg: ChatMsg) => {
    if (isGuest || !channelId || savedIds.current.has(msg.id)) return
    savedIds.current.add(msg.id)
    const isOwn = msg.sender === localParticipant.identity
    const text = isOwn ? msg.text : `[${msg.senderName}] ${msg.text}`
    try {
      await apiFetch('/api/messaging/channel-messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ room_id: channelId, msg: text }),
      })
    } catch { /* ignore persist errors */ }
  }, [isGuest, channelId, localParticipant.identity])

  const publish = useCallback((data: ChatDataMessage) => {
    if (!room?.localParticipant) return
    try {
      room.localParticipant.publishData(
        encoder.current.encode(JSON.stringify(data)),
        { reliable: true }
      )
    } catch { /* ignore */ }
  }, [room])

  // Listen for incoming chat data
  useEffect(() => {
    if (!room) return
    const handler = (payload: Uint8Array) => {
      try {
        const data: ChatDataMessage = JSON.parse(decoder.current.decode(payload))
        if (data.type !== 'visio-chat') return

        if (data.action === 'message' && data.message) {
          historyRef.current = [...historyRef.current, data.message]
          setMessages([...historyRef.current])
          // Persist received messages from others
          persistToMessaging(data.message)
        } else if (data.action === 'sync' && data.messages && !syncReceived.current) {
          syncReceived.current = true
          historyRef.current = [...data.messages]
          setMessages([...historyRef.current])
        }
      } catch { /* ignore */ }
    }
    room.on(RoomEvent.DataReceived, handler)
    return () => { room.off(RoomEvent.DataReceived, handler) }
  }, [room, persistToMessaging])

  // Sync history to new participants
  useEffect(() => {
    if (!room) return
    const onJoin = (participant: any) => {
      if (historyRef.current.length === 0) return
      try {
        const syncData: ChatDataMessage = {
          type: 'visio-chat',
          action: 'sync',
          messages: historyRef.current,
        }
        room.localParticipant.publishData(
          encoder.current.encode(JSON.stringify(syncData)),
          { reliable: true, destinationIdentities: [participant.identity] }
        )
      } catch { /* ignore */ }
    }
    room.on(RoomEvent.ParticipantConnected, onJoin)
    return () => { room.off(RoomEvent.ParticipantConnected, onJoin) }
  }, [room])

  // Auto-scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  const send = () => {
    const text = input.trim()
    if (!text) return
    const msg: ChatMsg = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      sender: localParticipant.identity,
      senderName: localParticipant.name || localParticipant.identity,
      text,
      ts: Date.now(),
    }
    historyRef.current = [...historyRef.current, msg]
    setMessages([...historyRef.current])
    setInput('')
    slog.action('chat-send', { len: text.length })
    publish({ type: 'visio-chat', action: 'message', message: msg })
    // Persist own message
    persistToMessaging(msg)
  }

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  return (
    <div className="absolute right-0 top-0 bottom-12 w-72 bg-zinc-900 border-l border-zinc-700 z-40 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-700">
        <span className="text-sm font-medium text-zinc-200">Chat</span>
        <button onClick={onClose} className="text-zinc-400 hover:text-white text-lg leading-none">&times;</button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
        {messages.length === 0 && (
          <div className="text-center text-zinc-500 text-xs py-4">Aucun message</div>
        )}
        {messages.map((msg) => (
          <div key={msg.id} className="text-xs">
            <div className="flex items-baseline gap-1.5">
              <span className="font-medium text-zinc-300">
                {msg.sender === localParticipant.identity ? 'Vous' : msg.senderName}
              </span>
              <span className="text-[10px] text-zinc-600">
                {new Date(msg.ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <div className="text-zinc-400 break-words whitespace-pre-wrap">{msg.text}</div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-2 py-2 border-t border-zinc-700 flex gap-1">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Message..."
          className="flex-1 px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-xs text-zinc-200 placeholder-zinc-600 outline-none focus:border-blue-500"
        />
        <button
          onClick={send}
          disabled={!input.trim()}
          className="px-2 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs rounded"
        >
          Envoyer
        </button>
      </div>
    </div>
  )
}
